import { yupResolver } from '@hookform/resolvers/yup';
import { Button, MenuItem } from '@mui/material';
import React, { FC, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ControlledCheckbox,
    ControlledInput,
    ControlledSelect,
    CustomAttributeType,
    FormGrid,
    IFormCustomAttributeConfiguration,
    IParams,
    Page,
    Section,
    useSelectedOrganisation,
    Widget,
} from '../../../shared';
import { useCustomAttributeConfigurationSchema } from '../../validators';
import {
    useCustomAttributeConfiguration,
    useSaveCustomAttributeConfiguration,
} from '../../hooks/custom-attributes-configuration.hook';

export const CustomAttributeConfigurationEditPage: FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams<keyof IParams>() as IParams;
    const { organisation } = useSelectedOrganisation();

    const { data: customAttributeConfiguration } = useCustomAttributeConfiguration(id);
    const { mutateAsync: saveCustomAttributeConfiguration } = useSaveCustomAttributeConfiguration();

    const form = useForm<IFormCustomAttributeConfiguration>({
        resolver: yupResolver(useCustomAttributeConfigurationSchema()),
        mode: 'all',
    });

    useEffect(() => {
        if (customAttributeConfiguration) {
            form.reset(customAttributeConfiguration);
        } else {
            form.reset({ type: Object.values(CustomAttributeType)[0] });
        }
    }, [form, customAttributeConfiguration]);

    const onSubmit = async (data: IFormCustomAttributeConfiguration) => {
        if (organisation) {
            await saveCustomAttributeConfiguration({
                data: {
                    ...data,
                    organisationId: organisation.id,
                    defaultSelected: data.type === CustomAttributeType.BOOLEAN ? data.defaultSelected : false,
                },
                messages: {
                    onUpdateSuccessMessage: t('updateCustomAttributeConfigurationSuccess'),
                    onUpdateErrorMessage: t('updateCustomAttributeConfigurationError'),
                    onCreateSuccessMessage: t('createCustomAttributeConfigurationSuccess'),
                    onCreateErrorMessage: t('createCustomAttributeConfigurationError'),
                },
            });
            navigate('/admin/custom-attribute-configurations');
        }
    };
    const actions = [
        <Button onClick={() => navigate('/admin/custom-attribute-configurations')} color="secondary">
            {t('cancel')}
        </Button>,
        <Button variant="contained" color="primary" onClick={form.handleSubmit(onSubmit)}>
            {t('save')}
        </Button>,
    ];

    return (
        <Page
            title={customAttributeConfiguration?.name || t('newCustomAttributeConfiguration')}
            actions={actions}
            onBack={() => navigate(-1)}
        >
            <FormProvider {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <Widget footer={[...actions].reverse()}>
                        <Section title={t('data')}>
                            <FormGrid>
                                <ControlledInput
                                    name="name"
                                    label={t('nameLabel')}
                                    required={true}
                                    sx={{ width: '400px' }}
                                />
                                <ControlledInput name="code" label={t('codeLabel')} required={true} />
                                <ControlledSelect name="type" label={t('typeLabel')} required={true}>
                                    {Object.values(CustomAttributeType).map((type) => (
                                        <MenuItem value={type} key={type}>
                                            {t(type)}
                                        </MenuItem>
                                    ))}
                                </ControlledSelect>
                            </FormGrid>
                            {form.watch('type') === CustomAttributeType.BOOLEAN && (
                                <ControlledCheckbox name="defaultSelected" label={t('defaultSelectedLabel')} />
                            )}
                        </Section>
                    </Widget>
                </form>
            </FormProvider>
        </Page>
    );
};
